import { SvgIconComponent } from "@mui/icons-material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Skeleton from "@mui/material/Skeleton";
import { Box, IconButton, styled, StyledEngineProvider, TableSortLabel, Typography } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import classNames from "classnames";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { TooltipV3 } from "../../component";
import { FONT_FAMILY, THEME_COLORS } from "../../themes";

const lodash = require("lodash");

const StyledTableHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#00000000",
  color: THEME_COLORS.black2,
}));

const tableCellStyles = makeStyles()((theme) => ({
  head: {
    backgroundColor: THEME_COLORS.sideMenuBg,
    color: THEME_COLORS.black2,
    marginTop: 8,
    marginBottom: 8,
  },
  body: {
    fontSize: 14,
    borderTop: `1px solid ${THEME_COLORS.grey1}`,
    borderBottom: `1px solid ${THEME_COLORS.grey1}`,
    textOverflow: "ellipsis",
    height: "3.6em",
    "&:first-child": {
      borderTopLeftRadius: 5,
      borderBottomLeftRadius: 5,
      borderLeft: `1px solid ${THEME_COLORS.grey1}`,
    },
    "&:last-child": {
      borderTopRightRadius: 5,
      borderBottomRightRadius: 5,
      borderRight: `1px solid ${THEME_COLORS.grey1}`,
    },
  },
}));

const StyledTableRow = styled(TableRow)`
  background-color: transparent;
`;

const statuses = ["WAITING FOR PARTS", "WORK START PENDING", "WORK IN PROGRESS", "TECH. ASSIGN. PENDING"];

function createData(name) {
  return {
    name,
    throughput: Math.floor(Math.random() * 2000),
    qcRejection: Math.floor(Math.random() * 100) % 100,
    avgTat: Math.floor(Math.random() * 500),
    avgCost: Math.floor(Math.random() * 455),
    breachedTat: Math.floor(Math.random() * 455),
    breachedCost: Math.floor(Math.random() * 455),
    i3Accuracy: Math.floor(Math.random() * 455) % 100,
    status: statuses[Math.floor(Math.random() * 455) % 4],
  };
}

let rows = [
  createData("Aman Joshi"),
  createData("Rahul Kumar"),
  createData("Ram Seth"),
  createData("Ramcharan"),
  createData("Kamal Raj"),
];

const useStyles = makeStyles<any>()((theme, props) => ({
  table: {
    minWidth: 700,
    borderCollapse: "separate",
    borderSpacing: "0 16px",
    overflowX: "initial",
    // tableLayout: "fixed",
  },
  tableContainer: {
    overflowY: "auto",
    "&:hover": {
      "&::-webkit-scrollbar-thumb": {
        WebkitBoxShadow: "inset 0 0 6px rgba(225,225,225,0.6)",
        backgroundColor: "rgba(128,128,128,0.6)",
      },
      "&::-webkit-scrollbar-track": {
        WebkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.3)",
        backgroundColor: "#F5F5F533",
      },
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: 8,
      WebkitBoxShadow: "none", //'inset 0 0 6px rgba(225,225,225,0.6)',
      backgroundColor: "transparent", //'rgba(0,0,0,0.6)',
    },
    "&::-webkit-scrollbar": {
      width: 8,
      height: 8,
    },
    "&::-webkit-scrollbar-track": {
      WebkitBoxShadow: "none", //'inset 0 0 6px rgba(0,0,0,0.3)',
      borderRadius: 10,
      backgroundColor: "transparent", // '#F5F5F533',
    },
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  tableHead: {
    fontFamily: FONT_FAMILY.semiBoldV3,
    backgroundColor: THEME_COLORS.white,
    minWidth: props.width ?? "100%",
    textTransform: "uppercase",
    overflow: "hidden",
  },
  status: {
    fontFamily: FONT_FAMILY.semiBoldV3,
  },
  customRow: {
    cursor: props.clickable ? "pointer" : "inherit",
    "&:hover": {
      backgroundColor: props.clickable ? THEME_COLORS.grey2 : "inherit",
    },
  },
  textContent: {
    display: "-webkit-box",
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    fontSize: "1em",
    wordBreak: "break-word",
  },
  headerContent: {
    display: "-webkit-box",
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 1,
    fontSize: "1em",
    fontFamily: FONT_FAMILY.boldV3,
  },
}));

export interface ColumnType<T> {
  name: string;
  label: string;
  sortable?: boolean;
  actionIcon?: SvgIconComponent;
  onAction?: (row: T) => void;
  component: (props: { row: T }) => JSX.Element;
  onSort?: (order: string) => void;
  align?: "left" | "right" | "center";
  borderColor?: string;
  rowColor?: (row: T) => string;
  width?: string;
}
export interface InventoryTableProps<T> {
  columnData: ColumnType<T>[];
  onRowClick: (row: T) => void;
  totalPages: number;
  page: number;
  data: { data: T[] };
  loading?: boolean;
  rowProps: { clickable: boolean };
  loadMore?: () => void;
  onChangeSort?: (label: string, order: "asc" | "desc") => void;
}

function InventoryTable<T>({
  columnData,
  data,
  loading,
  onRowClick,
  page = 1,
  totalPages,
  rowProps,
  loadMore,
  onChangeSort,
}: InventoryTableProps<T>) {
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const { classes } = useStyles(rowProps);
  const [pages, setPages] = useState(1);
  const [statusColumn] = useState(columnData?.find((column) => column.name === "Status"));
  const [distanceBottom, setDistanceBottom] = useState(0);
  const [hasMore] = useState(page != totalPages);
  const { classes: tableStyles } = tableCellStyles();
  const scrollTop = useRef(0);
  const rows = data?.data || [];

  useEffect(() => {
    if (totalPages != 0 && pages != totalPages) setPages(totalPages);
  }, [totalPages]);

  const onSort = (orderId: string) => {
    const nextOrderBy = orderBy == orderId ? "" : orderId;
    const nextOrder = order === "desc" ? "asc" : "desc";

    setOrderBy(nextOrderBy);
    setOrder(nextOrder);

    onChangeSort?.(nextOrderBy, nextOrder);
  };

  const scrollListener = useCallback(
    (ev) => {
      if (!loadMore) return;
      const {
        nativeEvent: { target },
      } = ev;
      let bottom = target.scrollHeight - target.clientHeight;
      const isDownDirection = scrollTop.current < ev.nativeEvent.target.scrollTop;
      scrollTop.current = ev.nativeEvent.target.scrollTop;

      // if you want to change distanceBottom every time new data is loaded
      // don't use the if statement
      if (!distanceBottom) {
        // list height thresold percentage of executing function = 50%
        setDistanceBottom(Math.round((bottom / 100) * 50));
      }

      if (isDownDirection && target.scrollTop > bottom - distanceBottom && hasMore && !loading) {
        loadMore();
      }
    },
    [hasMore, loadMore, loading, distanceBottom]
  );

  return (
    <StyledEngineProvider injectFirst>
      <TableContainer
        style={{ maxHeight: "calc(100vh - 240px)" }}
        onScroll={scrollListener}
        className={classes.tableContainer}
      >
        <Table stickyHeader className={classes.table}>
          <TableHead>
            <TableRow style={{ height: "1.2em" }}>
              {columnData.map((entity, index) => (
                <StyledTableHeader
                  className={classes.tableHead}
                  align={entity.align || "left"}
                  key={entity.name}
                  sortDirection={"asc"}
                >
                  {entity.sortable ? (
                    <TableSortLabel
                      active={orderBy === entity.label}
                      direction={orderBy === entity.label ? order : "asc"}
                      onClick={() => {
                        entity.onSort?.(order);
                        onSort(entity.label);
                      }}
                      IconComponent={(props) => {
                        return orderBy === entity.label ? (
                          <ExpandLessIcon {...props} fontSize="medium" />
                        ) : (
                          <ExpandMoreIcon color="disabled" fontSize="medium" />
                        );
                      }}
                    >
                      <TooltipV3 title={entity.name}>
                        <Typography className={classes.headerContent}>{entity.name}</Typography>
                      </TooltipV3>
                      {orderBy === entity.label ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    <TooltipV3 title={entity.name}>
                      <Typography className={classes.headerContent}>{entity.name}</Typography>
                    </TooltipV3>
                  )}
                </StyledTableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!!rows.length &&
              rows.map((row: any) => (
                <StyledTableRow
                  className={classes.customRow}
                  // key={row.id}
                  onClick={(ev) => {
                    ev.preventDefault();
                    if (onRowClick) onRowClick(row);
                  }}
                >
                  {columnData.map((column, index) => {
                    const { actionIcon: ActionIcon, onAction } = column;
                    const CellComponent = typeof column?.component == "function" ? column?.component : undefined;
                    const rowColor = statusColumn && statusColumn.rowColor && statusColumn.rowColor(row);

                    return (
                      <TableCell
                        key={row.id + column.name}
                        align={column?.align ?? (index === 0 ? "left" : columnData.length == 2 ? "right" : "left")}
                        component="th"
                        scope="row"
                        style={{
                          borderLeft:
                            statusColumn && index == 0 && `8px solid ${rowColor ?? row?.statusDisplay?.[0]?.color}`,
                          width: column?.width ?? "auto",
                        }}
                        classes={{
                          head: tableStyles.head,
                          body: tableStyles.body,
                        }}
                      >
                        {CellComponent ? (
                          <CellComponent row={row} />
                        ) : column.name == "Status" ? (
                          <Box
                            style={{
                              backgroundColor: `${row?.statusDisplay?.[0]?.color}33`,
                              color: row?.statusDisplay?.[0]?.color,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              borderRadius: 5,
                              padding: 8,
                              overflow: "hidden",
                            }}
                            height={30}
                          >
                            <TooltipV3 title={lodash.get(row, column.label)}>
                              <Typography className={classNames(classes.status, classes.textContent)}>
                                {lodash.get(row, column.label)?.toUpperCase()}
                              </Typography>
                            </TooltipV3>
                          </Box>
                        ) : (
                          <TooltipV3 title={lodash.get(row, column.label) ?? "Not available"}>
                            <Typography className={classes.textContent} variant="body1">
                              {lodash.get(row, column.label) ?? "Not available"}
                            </Typography>
                          </TooltipV3>
                        )}
                        {ActionIcon && onAction && (
                          <IconButton size="small" onClick={() => onAction(row)}>
                            <ActionIcon color="primary" fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    );
                  })}
                </StyledTableRow>
              ))}
            {loading &&
              [1, 2, 3].map((row) => (
                <StyledTableRow key={row}>
                  {columnData.map((column, index) => (
                    <TableCell
                      key={column.name + index}
                      align={index === 0 ? "left" : columnData.length == 2 ? "right" : "center"}
                      component="th"
                      scope="row"
                      classes={{
                        head: tableStyles.head,
                        body: tableStyles.body,
                      }}
                      width={column.width}
                    >
                      <Skeleton variant="rectangular" width="100%" height={42} />
                    </TableCell>
                  ))}
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </StyledEngineProvider>
  );
}

export default memo(InventoryTable) as typeof InventoryTable;
